// Helpers for the admin-only "Download Google Ads Sheet" export in the Export Leads popup.
//
// The application is US-only, so every value in this sheet is US Pacific time
// (America/Los_Angeles), whatever timezone the downloading PC is set to. The regular
// Leads Export formats dates in the browser's own timezone; nothing here reuses it.
//
// Kept free of React and antd so the helpers can be run directly with Node.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as XLSX from 'xlsx';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PACIFIC_TZ = 'America/Los_Angeles';

// Exact leadStage values (Lead model enum in ci-crm-backend/Models/Lead.js) for the
// outcomes the Google Ads team reports: Sale Made, Callback and Follow-up.
   export const GOOGLE_ADS_LEAD_STAGES = ['Sales Made', 'Call Back', 'Follow-Up', 'Quoted'];

export const GOOGLE_ADS_SHEET_NAME = 'Google Ads Leads';
export const GOOGLE_ADS_HEADERS = ['Google Click ID', 'Email', 'Phone Number', 'Conversion Time', 'Lead Status'];

export const GOOGLE_ADS_MAX_LOOKBACK_DAYS = 63;
export const GOOGLE_ADS_LOOKBACK_WARNING =
  'Google Ads only accepts enhanced conversions up to 63 days after the ad click. Older rows may be rejected.';

// Sent as the existing `fields` projection of GET /leads/allleads, so the export
// receives only what it writes instead of complete lead documents.
const GOOGLE_ADS_LEAD_FIELDS = ['personalInfo.email', 'personalInfo.phoneNumber', 'leadStage', 'createdAt', 'landingUrl'];

// createdAt is a UTC instant (Mongoose timestamps). It is parsed with dayjs.utc so
// that even a string without a "Z" is never read in the browser's local time.
export const formatConversionTime = (createdAt) => {
  if (!createdAt) return '';

  const instant = dayjs.utc(createdAt);
  if (!instant.isValid()) return '';

  return instant.tz(PACIFIC_TZ).format('YYYY-MM-DD[T]HH:mm:ss') + ` ${PACIFIC_TZ}`;
};

// The Ads sites save the full landing page URL (landingUrl), so the gclid is read
// from its query string. Leads without one (SEO, inbound calls, 'UNKNOWN', 'N/A')
// get an empty cell.
export const extractGclid = (landingUrl) => {
  if (!landingUrl || typeof landingUrl !== 'string') return '';

  try {
    return (new URL(landingUrl.trim()).searchParams.get('gclid') || '').trim();
  } catch {
    const match = landingUrl.match(/[?&#]gclid=([^&#\s]+)/i);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]).trim();
    } catch {
      return match[1].trim();
    }
  }
};

export const normalizeEmail = (email) => {
  if (email === null || email === undefined) return '';
  return String(email).trim().toLowerCase();
};

// E.164 for US numbers; any other shape is kept as entered (trimmed).
export const normalizePhoneE164 = (phone) => {
  if (phone === null || phone === undefined) return '';

  const original = String(phone).trim();
  if (!original) return '';

  const digits = original.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  return original;
};

const toEpochMs = (createdAt) => {
  const instant = createdAt ? dayjs.utc(createdAt) : null;
  return instant && instant.isValid() ? instant.valueOf() : Number.NEGATIVE_INFINITY;
};

// One row per lead whose outcome is Sale Made, Callback or Follow-up, newest first.
// Every matching lead is exported: a missing email or phone leaves that cell empty.
export const buildGoogleAdsRows = (leads) =>
  (Array.isArray(leads) ? leads : [])
    .filter((lead) => GOOGLE_ADS_LEAD_STAGES.includes(lead?.leadStage))
    .map((lead) => ({
      time: toEpochMs(lead.createdAt),
      row: [
        extractGclid(lead.landingUrl),
        normalizeEmail(lead.personalInfo?.email),
        normalizePhoneE164(lead.personalInfo?.phoneNumber),
        formatConversionTime(lead.createdAt),
        lead.leadStage || ''
      ]
    }))
    .sort((a, b) => b.time - a.time)
    .map(({ row }) => row);

// Every cell, header included, is a text cell (type 's', number format '@'), so Excel
// and Google Sheets keep the "+" on phone numbers and leave Conversion Time as text.
const toTextCell = (value) => ({
  t: 's',
  v: value === null || value === undefined ? '' : String(value),
  z: '@'
});

export const buildGoogleAdsWorkbook = (rows) => {
  const table = [GOOGLE_ADS_HEADERS, ...rows];
  const worksheet = {};

  table.forEach((cells, r) => {
    cells.forEach((value, c) => {
      worksheet[XLSX.utils.encode_cell({ r, c })] = toTextCell(value);
    });
  });

  worksheet['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: table.length - 1, c: GOOGLE_ADS_HEADERS.length - 1 }
  });
  worksheet['!cols'] = [{ wch: 60 }, { wch: 36 }, { wch: 16 }, { wch: 42 }, { wch: 18 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, GOOGLE_ADS_SHEET_NAME);
  return workbook;
};

// bookSST stores the text in the shared-strings table (<c t="s">), the standard
// representation of text cells.
export const writeGoogleAdsWorkbook = (workbook, fileName) =>
  XLSX.writeFile(workbook, fileName, { bookType: 'xlsx', bookSST: true });

// The date picker yields local-midnight values. Their calendar date is the day the user
// picked, and the server reads that date as a Pacific day (LeadController.getAll uses
// moment.tz(..., 'America/Los_Angeles')), exactly as for the regular Leads Export.
// The date is kept as picked: converting the local-midnight instant with .tz() would
// move it a day earlier on PCs east of Pacific.
const toPickedDay = (pickedDate) => dayjs(pickedDate).format('YYYY-MM-DD');
const toPacificDay = (pickedDate) => dayjs.tz(toPickedDay(pickedDate), PACIFIC_TZ);

// Same selection as the regular export (createdAt, whole Pacific days) but without the
// My Leads page filters, so every domain and every agent is included.
export const buildGoogleAdsFetchParams = (startDate, endDate) => ({
  page: 1,
  limit: 1000000,
  dateField: 'createdAt',
  startDate: `${toPickedDay(startDate)} 00:00:00`,
  endDate: `${toPickedDay(endDate)} 23:59:59`,
  fields: GOOGLE_ADS_LEAD_FIELDS.join(',')
});

// Response shapes accepted by LeadDownload.jsx for API.leadGetAll; null means the
// request failed.
export const extractLeadsFromResponse = (response) => {
  if (response && (response.status === true || response.success === true || response.statusCode === 200)) {
    return response.data || response.leads || [];
  }
  if (Array.isArray(response)) return response;
  if (response?.data && Array.isArray(response.data)) return response.data;
  return null;
};

export const getGoogleAdsFileName = (startDate, endDate) =>
  `Google_Ads_Leads_${toPacificDay(startDate).format('MMDDYYYY')}-${toPacificDay(endDate).format('MMDDYYYY')}.xlsx`;

// Whole days between the picked start date and today's date in Pacific time.
export const getDaysBeforeTodayPacific = (startDate, now = dayjs()) => {
  const todayPacific = dayjs.utc(now).tz(PACIFIC_TZ).format('YYYY-MM-DD');
  return dayjs.utc(todayPacific).diff(dayjs.utc(toPickedDay(startDate)), 'day');
};

export const isBeyondGoogleAdsLookback = (startDate, now = dayjs()) =>
  getDaysBeforeTodayPacific(startDate, now) > GOOGLE_ADS_MAX_LOOKBACK_DAYS;
